/**
 * Kronologjia e një personi nëpër aplikime.
 *
 * Një njeri mund të ketë aplikuar tri herë në tre vite për tri pozita. Ato
 * mbeten tre aplikime të ndara — secili është fotografia e momentit të vet — por
 * lexohen si një jetë e vetme: kur erdhi, deri ku shkoi, pse u ndal.
 *
 * Ajo që NUK shfaqet: komentet e brendshme të refuzimit dhe shënimet e
 * intervistuesve nuk dalin kurrë jashtë kësaj faqeje — dhe kjo faqe kërkon të
 * drejtën e rekrutimit.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Link } from 'react-router-dom';
import recruitmentHistoryService from '../../services/recruitmentHistoryService';
import { statusLabel, RECRUITMENT_STATUSES } from '../../config/recruitmentStatuses';
import { rejectionLabel, offerStatusMeta, interviewStatusMeta } from '../../config/recruitmentPipeline';
import { sourceLabel } from '../../config/recruitmentSources';

const STATUS_COLOR = Object.fromEntries(RECRUITMENT_STATUSES.map((x) => [x.key, x.color]));

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};
const yearOf = (d) => (d ? String(new Date(d).getFullYear()) : '—');

/** Sa e vjetër është kjo e dhënë — sepse disponueshmëria e 2025-ës nuk vlen sot. */
function ageText(d) {
  if (!d) return '';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (isNaN(days)) return '';
  if (days < 31) return `${days} ditë më parë`;
  const months = Math.floor(days / 30);
  if (months < 24) return `${months} muaj më parë`;
  return `${Math.floor(months / 12)} vjet më parë`;
}

const INVITE_STATUS = {
  invited: { label: 'E dërguar', color: 'info' },
  interested: { label: 'I interesuar', color: 'success' },
  declined: { label: 'Nuk është i interesuar', color: 'default' },
  expired: { label: 'E skaduar', color: 'default' },
  converted: { label: 'U bë aplikim', color: 'primary' },
};

export default function CandidateHistoryDrawer({ candidateId, open, onClose, postings = [], onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [invitePosting, setInvitePosting] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [flash, setFlash] = useState('');

  const load = async (id) => {
    setLoading(true); setError('');
    const d = await recruitmentHistoryService().candidate(id);
    if (d.error) setError(d.error); else setData(d);
    setLoading(false);
  };

  useEffect(() => {
    if (open && candidateId) { setFlash(''); setInvitePosting(''); setInviteNote(''); load(candidateId); }
    if (!open) setData(null);
  }, [open, candidateId]);

  const run = async (fn, okText) => {
    setBusy(true); setError(''); setFlash('');
    const r = await fn();
    setBusy(false);
    if (r?.error) { setError(r.error); return null; }
    setFlash(okText);
    await load(candidateId);
    if (onChanged) onChanged();
    return r;
  };

  const c = data?.candidate;
  const elig = data?.eligibility;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 0 } }}>
      <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 2, px: 2.5, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonOutlineIcon sx={{ color: '#3E8FA3' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 800 }}>{c?.name || 'Kandidati'}</Typography>
            <Typography noWrap variant="caption" color="text.secondary">
              {[c?.email, c?.phone].filter(Boolean).join(' · ') || 'pa kontakt të regjistruar'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>
        ) : !data ? (
          error ? <Alert severity="error">{error}</Alert> : null
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            {flash && <Alert severity="success" onClose={() => setFlash('')}>{flash}</Alert>}

            {c.needsReview && (
              <Alert severity="warning" icon={<WarningAmberRoundedIcon fontSize="inherit" />}>
                Identifikuesit e këtij personi bien ndesh me një tjetër. Asgjë nuk u bashkua automatikisht —
                {c.reviewNote ? ` ${c.reviewNote}` : ' shqyrtoje para se ta përdorësh për vendime.'}
              </Alert>
            )}

            {/* Vjetërsia e të dhënave shfaqet gjithmonë: kjo është arsyeja pse
                veprimi i parë është ftesë e jo ofertë. */}
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">Aplikimi i fundit</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {fmtDate(data.lastAppliedAt)}{' '}
                <Typography component="span" variant="caption" color="text.secondary">{ageText(data.lastAppliedAt)}</Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Të dhënat janë ato të deklaruara atëherë — disponueshmëria dhe pritshmëria për pagën duhen konfirmuar sërish.
              </Typography>
            </Box>

            {/* ── Ftesa për një pozitë ────────────────────────────────────── */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Fto për një pozitë</Typography>
              {!elig?.eligible ? (
                <Alert severity="info">{elig?.reason || 'Ftesa nuk lejohet për këtë kandidat.'}</Alert>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">{elig.reason}</Typography>
                  <TextField select size="small" label="Pozita" value={invitePosting}
                    onChange={(e) => setInvitePosting(e.target.value)} fullWidth>
                    {postings.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.title}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="Shënim (opsional)" value={inviteNote} multiline minRows={2}
                    onChange={(e) => setInviteNote(e.target.value)} fullWidth />
                  <Button size="small" variant="contained" disabled={!invitePosting || busy}
                    onClick={() => run(
                      () => recruitmentHistoryService().invite(candidateId, Number(invitePosting), inviteNote),
                      'Ftesa u regjistrua. Aplikimi krijohet vetëm pasi personi shpreh interes.')}>
                    Fto për këtë pozitë
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Ftesa nuk është ofertë dhe nuk dërgon asgjë vetvetiu.
                  </Typography>
                </Stack>
              )}
            </Box>

            {data.invitations.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Ftesat</Typography>
                <Stack spacing={1}>
                  {data.invitations.map((i) => {
                    const meta = INVITE_STATUS[i.status] || INVITE_STATUS.invited;
                    return (
                      <Box key={i.id} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{i.postingTitle || `Pozita #${i.jobPostingId}`}</Typography>
                          <Chip size="small" color={meta.color} label={meta.label} sx={{ height: 20, fontSize: '.66rem' }} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(i.invitedAt)}{i.invitedBy ? ` · ${i.invitedBy}` : ''}
                        </Typography>
                        {i.note && <Typography variant="body2" sx={{ fontSize: 12.5, mt: 0.5 }}>{i.note}</Typography>}
                        {i.newApplicationId && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            <Link to={`/applications/edit/${i.newApplicationId}`}>Aplikimi i ri #{i.newApplicationId}</Link>
                          </Typography>
                        )}
                        {['invited', 'interested'].includes(i.status) && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                            {i.status === 'invited' && (
                              <Button size="small" disabled={busy}
                                onClick={() => run(() => recruitmentHistoryService().respond(i.id, 'interested'), 'U shënua si i interesuar.')}
                                sx={{ textTransform: 'none', fontSize: '.72rem' }}>Është i interesuar</Button>
                            )}
                            <Button size="small" color="inherit" disabled={busy}
                              onClick={() => run(() => recruitmentHistoryService().respond(i.id, 'declined'), 'U shënua si i painteresuar.')}
                              sx={{ textTransform: 'none', fontSize: '.72rem' }}>Nuk është</Button>
                            <Tooltip title="Krijon një aplikim TË RI për këtë pozitë. Aplikimi i vjetër nuk preket.">
                              <Button size="small" variant="outlined" disabled={busy}
                                onClick={async () => {
                                  const r = await run(() => recruitmentHistoryService().createApplication(i.id),
                                    'Aplikimi i ri u krijua. Dokumentet nuk u kopjuan.');
                                  if (r?.applicationId) setFlash(`Aplikimi i ri #${r.applicationId} u krijua. Dokumentet nuk u kopjuan — kërko CV të re.`);
                                }}
                                sx={{ textTransform: 'none', fontSize: '.72rem' }}>Krijo aplikim</Button>
                            </Tooltip>
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Divider />

            {/* ── Kronologjia ─────────────────────────────────────────────── */}
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Historiku i aplikimeve</Typography>
            <Stack spacing={1.5}>
              {data.applications.map((a) => {
                const ivs = data.interviews.filter((x) => x.application_id === a.id);
                const ofs = data.offers.filter((x) => x.application_id === a.id);
                return (
                  <Box key={a.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Chip size="small" label={yearOf(a.appliedAt)} sx={{ height: 20, fontSize: '.66rem', fontWeight: 800 }} />
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1 }}>{a.position || 'Pa pozitë'}</Typography>
                      <Chip size="small" color={STATUS_COLOR[a.status] || 'default'} label={statusLabel(a.status)} sx={{ height: 20, fontSize: '.66rem' }} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDate(a.appliedAt)} · {sourceLabel(a.source)}
                      {a.postingActive === false ? ' · pozita është mbyllur' : ''}
                      {a.documentCount ? ` · ${a.documentCount} dokument(e) nga atëherë` : ' · pa dokumente'}
                    </Typography>
                    {a.status === 'rejected' && a.rejectionCode && (
                      <Typography variant="body2" sx={{ fontSize: 12.5, mt: 0.5 }}>
                        Refuzuar te faza «{statusLabel(a.rejectedStage || 'applied')}» — {rejectionLabel(a.rejectionCode)}
                      </Typography>
                    )}
                    {ivs.map((iv) => {
                      const m = interviewStatusMeta(iv.status);
                      return (
                        <Typography key={iv.id} variant="body2" sx={{ fontSize: 12.5, mt: 0.25, color: 'text.secondary' }}>
                          Intervista {iv.sequence} · {fmtDate(iv.scheduled_at)} · {m.label}
                          {iv.rating ? ` · vlerësim ${iv.rating}/5` : ''}
                        </Typography>
                      );
                    })}
                    {ofs.map((of) => {
                      const m = offerStatusMeta(of.status);
                      return (
                        <Typography key={of.id} variant="body2" sx={{ fontSize: 12.5, mt: 0.25, color: 'text.secondary' }}>
                          Ofertë #{of.id} · {m.label}{of.start_date ? ` · fillimi ${fmtDate(of.start_date)}` : ''}
                        </Typography>
                      );
                    })}
                    <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                      <Link to={`/applications/edit/${a.id}`} style={{ fontSize: 12, color: '#2C6E7E', fontWeight: 600 }}>Hap aplikimin</Link>
                      {a.workerId && (
                        <Link to={`/workerlist/editworker/${a.workerId}`} style={{ fontSize: 12, color: '#2C6E7E', fontWeight: 600 }}>Punonjësi</Link>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
